<script lang="ts" module>
  export const gameOverModal = new ModalState('game-over', { layer: 800 });
</script>

<script lang="ts">
  import DailyTimer, { timer } from '~/components/DailyTimer.svelte';
  import ShareButton from '~/share/ShareButton.svelte';
  import { prettyDate, getTimeTilNextPuzzle } from '~/utils/dates';
  import FinalStars from '~/gameOver/FinalStars.svelte';
  import {
    getLatestPuzzleKey,
    isPremium,
    latestDailyQuery,
  } from '~/lib/queries.svelte';
  import PlayDailyButton from '~/components/buttons/PlayDailyButton.svelte';
  import { type DailyPuzzle, type Match } from '~/types';
  import { useQueryClient } from '@tanstack/svelte-query';
  import GameSummary from '~/gameOver/GameSummary.svelte';
  import { ModalState } from '~/lib/nav.svelte';
  import ArchiveButton from '~/components/buttons/ArchiveButton.svelte';
  import SlidePanel from '~/components/SlidePanel.svelte';
  import ExpandButton from '~/components/buttons/ExpandButton.svelte';
  import SplashLogo from '~/components/SplashLogo.svelte';
  import HomeButton from '~/components/buttons/HomeButton.svelte';
  import ShareModal from '~/share/ShareModal.svelte';
  import { DailyGame } from '~/store/daily.svelte';
  import { getGame } from '~/store/GameContext.svelte';

  type Props = {
    words: Match[];
  };
  const props: Props = $props();
  const premium = isPremium();
  const queryClient = useQueryClient();
  const game = $derived(getGame<DailyGame>().current);

  const latestDaily = $derived(latestDailyQuery(game.variant));

  const handleToggle = async (open: boolean) => {
    if (open) {
      await gameOverModal.push();
    } else {
      await gameOverModal.pop();
    }
  };

  const isLatestDaily = $derived(game.id === latestDaily.data?.id);

  // The local game is over, but the server may not know it yet: the save
  // mutation is still in flight, it failed, or an anonymous player's userId
  // cookie changed. Without `completed`, the timer never starts, the
  // refetchInterval never polls and puzzleExpired stays false, which leaves
  // returning players stuck on yesterday's puzzle. Trust the local state.
  $effect(() => {
    const data = latestDaily.data;
    if (game.lost && data && data.id === game.id && !data.completed) {
      queryClient.setQueryData<DailyPuzzle>(
        ['daily', getLatestPuzzleKey(game.variant)],
        { ...data, completed: true },
      );
    }
  });
  // True when the player's completed puzzle has aged past its 24-hour window but
  // latestDaily.data hasn't been updated yet (refetch in-flight or new puzzle not
  // yet published). In that state the timer never started and isLatestDaily is
  // still true, so we need this extra check to un-stick the "Today's Puzzle" button.
  const puzzleExpired = $derived(
    !!latestDaily.data?.completed &&
    getTimeTilNextPuzzle(latestDaily.data.date) <= 0
  );
</script>

<ShareModal words={props.words} />
<SlidePanel open={gameOverModal.open} onToggle={handleToggle} zIndex={800}>
  {#snippet title()}
    <div class="handle-title">
      <SplashLogo animate gameOver />
      {#if !isLatestDaily}
        <span class="date-text">
          {#if game.date}
            {prettyDate(game.date)}
          {:else if game.key}
            {game.key}
          {/if}
        </span>
      {/if}
    </div>
  {/snippet}
  {#snippet handle()}
    <h1 class="title-container">
      <ExpandButton open={gameOverModal.open} />
      <div>
        <FinalStars stars={game.stars!} bonus={game.fullClear} />
      </div>
    </h1>
  {/snippet}
  {#snippet content()}
    <div class="content">
      <GameSummary words={props.words} {isLatestDaily} />
    </div>
  {/snippet}
  {#snippet controls()}
    <DailyTimer variant={game.variant} />
    <PlayDailyButton newPuzzle={$timer === 0 && (!isLatestDaily || puzzleExpired)} />
    {#if isLatestDaily}
      <ShareButton />
    {/if}
    {#if premium.data}
      <ArchiveButton />
    {/if}
    <HomeButton />
  {/snippet}
</SlidePanel>

<style lang="scss">
  .handle-title {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .title-container {
    margin: 0;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
  }
  .date-text {
    height: 1rem;
    font-size: 1rem;
    font-weight: 500;
    opacity: 60%;
  }
  .content {
    padding: 1em;
    width: 100%;
    text-align: center;
  }
</style>
